import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import {
  CUSTOM_RANGE_ID,
  DEFAULT_RANGE_ID,
  MAX_CUSTOM_RANGE_MONTHS,
  RANGE_PRESETS,
  getCustomRangeLimits,
  getPresetWindow,
  toMonthValue,
  validateCustomRange,
} from '../utils'
import DateRangePicker from './date-range-picker'

const toDays = ({ startDate, endDate }) => ({
  start: DateTime.fromISO(startDate),
  end: DateTime.fromISO(endDate),
})

// Charts and queries are monthly, so a picked range counts whole months: the
// first day's month through the last day's month.
const DateRangeFilter = ({ range, onSelect }) => {
  const presets = useMemo(
    () =>
      RANGE_PRESETS.map(({ id, label }) => ({
        id,
        label,
        ...toDays(getPresetWindow(id)),
      })),
    [],
  )
  const { minDate, maxDate } = getCustomRangeLimits()

  const apply = ({ start, end, presetId }) => {
    if (presetId) return onSelect({ preset: presetId })

    const selection = { start: toMonthValue(start), end: toMonthValue(end) }
    if (!validateCustomRange(selection)) onSelect(selection)
  }

  return (
    <DateRangePicker
      label="Date range"
      displayValue={range.presetLabel}
      isActive={range.id !== DEFAULT_RANGE_ID}
      value={toDays(range)}
      presets={presets}
      selectedPresetId={range.id === CUSTOM_RANGE_ID ? null : range.id}
      minDate={minDate}
      maxDate={maxDate}
      maxMonths={MAX_CUSTOM_RANGE_MONTHS}
      onApply={apply}
    />
  )
}

DateRangeFilter.propTypes = {
  range: PropTypes.shape({
    id: PropTypes.string.isRequired,
    presetLabel: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
}

export default DateRangeFilter
